import { Typography, Pagination, Stack, Box } from "@mui/material";
import {
  AutostoreTable,
  ConfirmationModal,
  formatDate,
  formatUtcDate,
  formatDateDayjs
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import { AutostoreBin } from "~/features/autostoreBin";
import InventoryAdjustDialog, {
  InventoryToModify
} from "~/features/inventoryAdjustDialog/InventoryAdjustDialog";
import { InventoryHoldModal } from "~/features/inventoryHoldModal/InventoryHoldModal";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";
import {
  generateLocationNameFromBin,
  inventoryDateLabel,
  getInventoryDateObj,
  checkIsExpiration,
  getHoldTypeOptions,
  formatHoldText,
  HoldType
} from "~/lib/helpers";
import { useGridV2Subscription } from "~/lib/signalr";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";
import {
  fetchPortStatus,
  openPort,
  closePort,
  createBinRequest,
  getNextBinWithConfig,
  closeBin,
  binAtPortEvent,
  binNotAtPort,
  fetchBinLogPublisherState,
  setNoMoreBins,
  closeWorkstation
} from "~/redux/actions/autostore";
import {
  fetchCycleCountsUncountedBins,
  postBinCount,
  clearCycleCountsState,
  completeCycleCounts,
  modifyUncountedBin,
  modifyShapedCycleCountInfo,
  clearCycleCountsMessage
} from "~/redux/actions/cycleCounts";
import { placeInventoryHold } from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectedInventoryInBin,
  selectInventoryAtPort,
  selectUncountedBinAtPort
} from "~/redux/selectors/autostoreCycleCountsSelectors";

import { useGetIncompleteCycleCountsQuery } from "~/redux/warehouse/cycleCount.hooks";
import {
  IncompleteCycleCountDto,
  AutostoreBinConfigurationDto,
  UncountedBinDto,
  AutostoreEvent
} from "~/types/api";

import { AutostoreCycleCountsToolbar } from "./AutostoreCycleCountsToolbar";

import {
  clearSelectedBinRow,
  clearSelectedRows,
  selectBinRow,
  selectRows,
  setBinPresent,
  setIsAdjustPanelOpen,
  setIsBinHoldModalOpen,
  setPage,
  setPageState,
  setPortOpened,
  setPortPollingActive
} from "./autostoreCycleCounts.slice";

const mapStateToProps = (state: StoreState) => ({
  selectedAutostoreGridId: state.workstations.siteWorkstation?.autostoreGridId,
  selectedPortId: state.workstations.sitePortId,
  portState: state.autostore.portState,
  loadingCycleCountBins: state.cycleCounts.loadingCycleCountBins,
  cycleCountsUncountedBins: state.cycleCounts.cycleCountsUncountedBins,
  shapedCycleCountInfos: state.cycleCounts.shapedCycleCountInfos,
  nextBinInventoryResponse: state.autostore.nextBinInventoryResponse,
  nextBinConfiguration: state.autostore.nextBinConfiguration,
  binAtPort: state.autostore.binAtPort,
  noMoreBins: state.autostore.noMoreBins,
  usersFulfillmentCenter: state.store.usersFulfillmentCenter,
  siteAllPortIds: state.workstations.siteAllPortIds,
  thisWorkstationId: state.workstations.siteWorkstation?.id
});

const connector = connect(mapStateToProps, {
  fetchPortStatus,
  openPort,
  closePort,
  createBinRequest,
  fetchCycleCountsUncountedBins,
  getNextBinWithConfig,
  closeBin,
  clearCycleCountsMessage,
  modifyUncountedBin,
  modifyShapedCycleCountInfo,
  postBinCount,
  clearCycleCountsState,
  completeCycleCounts,
  binAtPortEvent,
  binNotAtPort,
  fetchBinLogPublisherState,
  setNoMoreBins,
  placeInventoryHold,
  closeWorkstation
});

type AutostoreCycleCountsInheritedProps = { viewTitle: ViewNameTranslation };
type PropsFromRedux = ConnectedProps<typeof connector>;
type AutostoreCycleCountsProps = PropsFromRedux &
  AutostoreCycleCountsInheritedProps;

function AutostoreCycleCounts(props: AutostoreCycleCountsProps) {
  const {
    selectedAutostoreGridId,
    selectedPortId,
    portState,
    cycleCountsUncountedBins,
    shapedCycleCountInfos,
    nextBinInventoryResponse,
    nextBinConfiguration,
    binAtPort,
    noMoreBins,
    siteAllPortIds,
    thisWorkstationId,
    viewTitle,
    clearCycleCountsState,
    setNoMoreBins,
    closeWorkstation
  } = props;

  const { t } = useTranslation();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  const dispatch = useAppDispatch();
  const { setToolbar } = useNavbar({ viewTitle });

  const { inv_inventoryDateLabel, ap_excludeRecalledHold } =
    useAppSelector(selectServerConfig);

  const isAdjustPanelOpen = useAppSelector(
    (state) => state.autostoreCycleCounts.isAdjustPanelOpen
  );
  const isBinHoldModalOpen = useAppSelector(
    (state) => state.autostoreCycleCounts.isBinHoldModalOpen
  );
  const pageState = useAppSelector(
    (state) => state.autostoreCycleCounts.pageState
  );
  const portOpened = useAppSelector(
    (state) => state.autostoreCycleCounts.portOpened
  );
  const portPollingActive = useAppSelector(
    (state) => state.autostoreCycleCounts.portPollingActive
  );
  const selectedBinRow = useAppSelector(
    (state) => state.autostoreCycleCounts.selectedBinRow
  );
  const selectedInventory = useAppSelector(selectedInventoryInBin);
  const selectedRows = useAppSelector(
    (state) => state.autostoreCycleCounts.selectedRows
  );
  const limit = useAppSelector((state) => state.autostoreCycleCounts.limit);
  const offset = useAppSelector((state) => state.autostoreCycleCounts.offset);
  const page = useAppSelector((state) => state.autostoreCycleCounts.page);

  useDevCheats({ isPortPolling: portPollingActive, showAutostoreStatus: true });

  // Local State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  // bin configuration info
  const [autostoreBinConfiguration, setAutostoreBinConfiguration] =
    useState<AutostoreBinConfigurationDto | null>(null);
  const [compartment, setCompartment] = useState<number | null>(null);

  const [binAtPortSeconds, setBinAtPortSeconds] = useState(0);

  // two methods to see what bin is coming next:
  // 1) match using next bin response and uncounted bins response
  // 2) while polling, compare the portState response bin
  // and match that to an uncounted bin

  // use method 1:

  // next bin response is an array, potentially with more than one inventory
  // find the inventory id that matches an uncounted bin's inventory id
  const matchingNextBinInventory = useAppSelector(selectInventoryAtPort);

  // above we get the inventory from nextResponse endpoint and uncounted bins endpoint
  // here we use the same sources to get the uncountedBin
  const matchingUncountedBin = useAppSelector(selectUncountedBinAtPort);

  // when we know what bin is coming, set the cycle count status
  useEffect(() => {
    if (!matchingUncountedBin) return;

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    props.modifyShapedCycleCountInfo({
      cycleCountId: matchingUncountedBin.cycleCountId,
      newCycleCountStatus: "active"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingUncountedBin]);

  // prepare inventory to modify for adjust inventory modal
  let selectedLocationName = "";

  if (matchingNextBinInventory) {
    selectedLocationName = generateLocationNameFromBin(
      matchingNextBinInventory.bin
    );
  }

  const invToModify: InventoryToModify = matchingNextBinInventory
    ? {
        binType: matchingNextBinInventory.bin.binType,
        binId: matchingNextBinInventory.bin.binId,
        inventoryId: matchingNextBinInventory.inventoryId,
        locationName: `Bin: ${selectedLocationName}`,
        count:
          matchingUncountedBin?.expectedCount || matchingNextBinInventory.count,
        committedCount: matchingNextBinInventory.committedCount,
        ...getInventoryDateObj(
          inv_inventoryDateLabel,
          dayjs(matchingNextBinInventory?.expiration) ||
            dayjs(matchingNextBinInventory?.manufactureDate)
        ),
        lotNumber: matchingNextBinInventory.lotNumber,
        variantId: matchingNextBinInventory.variantId
      }
    : null;

  const [allCycleCountsComplete, setAllCycleCountsComplete] =
    useState<boolean>(false);

  const {
    incompleteCycleCounts,
    incompleteCycleCountsCount,
    loadingIncompleteCycleCounts,
    isUninitialized,
    refetch
  } = useGetIncompleteCycleCountsQuery(
    { offset, limit, autostoreGridId: selectedAutostoreGridId },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, isFetching, isUninitialized }) => ({
        incompleteCycleCounts: data?.items ?? [],
        incompleteCycleCountsCount: data?.totalCount,
        loadingIncompleteCycleCounts: isFetching,
        isUninitialized
      })
    }
  );

  const totalPageCount = incompleteCycleCountsCount
    ? Math.ceil(incompleteCycleCountsCount / limit)
    : 0;

  // fetch initial cycle counts
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (selectedAutostoreGridId) {
      void dispatch(closePort());
      dispatch(setPortOpened(false));
    }
  }, [selectedAutostoreGridId, dispatch]);

  // pagination
  useEffect(() => {
    void dispatch(closePort());
    dispatch(setPortOpened(false));
  }, [limit, page, dispatch]);

  // reset page logic
  const resetPage = useCallback(() => {
    dispatch(clearSelectedRows());
    clearCycleCountsState();
    dispatch(setPortPollingActive(false));
    dispatch(setBinPresent(false));
    dispatch(setIsAdjustPanelOpen(false));
    setIsConfirmModalOpen(false);
    setNoMoreBins(false);
    dispatch(setPage(1));
    dispatch(setPageState("initial"));
    if (selectedAutostoreGridId && selectedPortId) {
      void closeWorkstation();
      dispatch(setPortOpened(false));
      if (!isUninitialized) void refetch();
    }
  }, [
    dispatch,
    clearCycleCountsState,
    setNoMoreBins,
    selectedAutostoreGridId,
    selectedPortId,
    closeWorkstation,
    isUninitialized,
    refetch
  ]);

  // component did unmount
  useEffect(
    () => () => {
      resetPage();
      if (shouldListenToGridEvents) {
        props.binNotAtPort();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // fires every time the page state changes
  // monitors when the bin has arrived
  useEffect(() => {
    if (pageState === "bin arrived") {
      dispatch(setPageState("bin here"));
      dispatch(setBinPresent(true));
    }
  }, [dispatch, pageState]);

  // portState polling
  usePromiseInterval(
    async () => {
      if (!selectedAutostoreGridId || !selectedPortId) return;

      await props.fetchPortStatus();
    },
    500,
    portPollingActive && !shouldListenToGridEvents
  );

  // Stop polling when a bin arrives
  useEffect(() => {
    // fires the page state "bin arrived" once when conditions are true
    if (
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      portState &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      portState.isReady &&
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      portState.selectedBin &&
      !shouldListenToGridEvents
    ) {
      if (pageState !== "bin here" && pageState !== "initial") {
        dispatch(setPageState("bin arrived"));
      }

      dispatch(setPortPollingActive(false));

      // commented out the page runs on a loop since portState gets updated, then binAtPort updates
      // props.binNotAtPort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portState]);

  useEffect(() => {
    if (selectedRows.length) {
      setToolbar(<AutostoreCycleCountsToolbar resetPage={resetPage} />);
    } else {
      setToolbar(undefined);
    }
  }, [resetPage, selectedRows.length, setToolbar]);

  // POLL FOR BIN AT PORT INFORMATION IF EVENT ISN'T RECEIVED LONGER THAN 7 SECONDS
  usePromiseInterval(
    async () => {
      setBinAtPortSeconds((binAtPortSecondsState) => binAtPortSecondsState + 1);
      if (binAtPortSeconds > 7) {
        const portStatus = await props.fetchPortStatus();
        if (portStatus && portStatus.isReady && selectedAutostoreGridId) {
          const binStateResponse = await props.fetchBinLogPublisherState(
            selectedAutostoreGridId,
            portStatus.selectedBin
          );

          if (
            binStateResponse?.binState.binMode === "O" &&
            binStateResponse?.binState.portId === selectedPortId
          ) {
            props.binAtPortEvent();
            dispatch(setPageState("bin arrived"));
            dispatch(setBinPresent(true));
          }
        }
      }
    },
    1000,
    !binAtPort && shouldListenToGridEvents && portOpened
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (binAtPort && binAtPortSeconds > 0) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setBinAtPortSeconds(0);
    }
    // autoselect the first row once the bin arrives
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (binAtPort && !selectedInventory && matchingUncountedBin) {
      dispatch(selectBinRow(matchingUncountedBin.inventoryId));
    }

    void props.fetchPortStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binAtPort]);

  // after the user clicks get bins,
  // then after uncounted bins are fetched, create a task group
  useEffect(() => {
    if (
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !cycleCountsUncountedBins.length ||
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !selectedAutostoreGridId ||
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      !selectedPortId ||
      // now that we edit cycleCountsUncountedBins, we can't run this every time it changes
      pageState === "bin arrived"
    )
      return;
    dispatch(
      setPageState(
        "create task group with autostore bins from cycleCountsUncountedBins"
      )
    );
    const categoryId = 7000 + selectedPortId;

    const asyncCreateBinRequestFlow = async () => {
      const binIds: number[] = cycleCountsUncountedBins
        .map(
          (cycleCountUncountedBin) =>
            cycleCountUncountedBin.bin.autostoreBin?.autostoreBinId || 0
        )
        .filter((bId) => !!bId);

      dispatch(setPageState("close port"));
      // close workstation to reset task-group state before creating a new task-group
      await closeWorkstation();

      dispatch(setPortOpened(false));

      // create task group with cycle counts category
      await props.createBinRequest({ binIds, categoryId });

      // open port with same category as cycle-counts and start polling port
      dispatch(setPageState("open port"));

      await props.openPort({ categoryId });
      dispatch(setPortOpened(true));

      await props.fetchPortStatus();
      dispatch(setPageState("start polling"));
      if (!shouldListenToGridEvents) {
        dispatch(setPortPollingActive(true));
      } else {
        props.binNotAtPort();
      }

      dispatch(setPageState("get next bin"));
      try {
        await props.getNextBinWithConfig();
      } catch {
        // todo: use toasts here
      }
    };

    void asyncCreateBinRequestFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleCountsUncountedBins]);

  // set bin configuration and compartment for the selected inventory
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setAutostoreBinConfiguration(nextBinConfiguration || null);

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setCompartment(
      matchingUncountedBin?.bin.autostoreCompartmentNumber
        ? matchingUncountedBin.bin.autostoreCompartmentNumber - 1
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextBinConfiguration]);

  // look for cycle counts with "ready to complete" status
  // defined in modifyShapedCycleCountInfo (cycleCounts reducer)
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!shapedCycleCountInfos.length) return;

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    const cycleCountIdsReadyToComplete = shapedCycleCountInfos
      .filter((cycleCountInfo) => cycleCountInfo.status === "ready to complete")
      .map((cycleCountInfo) => cycleCountInfo.cycleCountId);

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (cycleCountIdsReadyToComplete.length) {
      void props.completeCycleCounts(
        cycleCountIdsReadyToComplete,
        selectedAutostoreGridId
      );
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setAllCycleCountsComplete(
      shapedCycleCountInfos.every(
        (cycleCountInfo) => cycleCountInfo.status === "complete"
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapedCycleCountInfos]);

  // after all bins are processed, open the confirmation modal
  // which will close and reset the page
  const showConfirmModal = allCycleCountsComplete || noMoreBins;
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (showConfirmModal) {
      dispatch(setPageState("all cycle counts complete"));
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setIsConfirmModalOpen(true);
    }
  }, [dispatch, showConfirmModal]);

  const completedCycleCountList = shapedCycleCountInfos
    .filter((cycleCountInfo) => cycleCountInfo.status === "complete")
    .map((cycleCountInfo) => {
      const variant =
        cycleCountInfo.uncountedBinsInfo[0]?.uncountedBin?.variant ??
        incompleteCycleCounts.find(
          (incompleteCycleCount) =>
            incompleteCycleCount.cycleCountId === cycleCountInfo.cycleCountId
        )?.variant;

      return variant?.productName ?? cycleCountInfo.cycleCountId;
    });

  const gridSub = async (data: AutostoreEvent) => {
    if (data.case !== "BinModeChange" || data.event.binMode !== "O") {
      return;
    }

    const portStatus = await props.fetchPortStatus();

    if (
      data.event.binId === portStatus?.selectedBin &&
      data.event.gridId === props.selectedAutostoreGridId &&
      !!data.event.portId &&
      siteAllPortIds.includes(data.event.portId)
    ) {
      // Dispatch redux action
      props.binAtPortEvent();
      dispatch(setPageState("bin arrived"));
      dispatch(setBinPresent(true));
    }
  };

  useGridV2Subscription(gridSub);

  return (
    <Box
      sx={{
        height: {
          xs: "auto",
          lg: "calc(100vh - 64px)"
        },
        maxWidth: (theme) => theme.breakpoints.values.xl,
        p: 5,
        boxSizing: "border-box",
        margin: "0 auto",
        rowGap: {
          xs: 4,
          lg: 3
        },
        columnGap: 3,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" }
      }}
    >
      {/* left side */}
      <Stack
        direction="column"
        gap={2}
        alignItems="center"
        sx={{
          gridColumn: { xs: "1 / -1", md: "1 / 2" },
          width: "100%",
          height: "100%"
        }}
      >
        {matchingUncountedBin ? (
          <ProductCard.Root
            productName={matchingUncountedBin.variant.productName ?? ""}
            imageFileName={matchingUncountedBin.variant.imageFilename}
            tempZone={matchingUncountedBin.variant.temperatureZone}
          >
            <ProductCard.Sku sku={matchingUncountedBin.variant.sku} />
            <ProductCard.ProductCount
              count={matchingUncountedBin.expectedCount}
            />
          </ProductCard.Root>
        ) : (
          <ProductCard.Loading height={480} />
        )}
        <Stack direction="column" width="100%" alignItems="center">
          {!portPollingActive && portState && portState.selectedBin ? (
            <Typography
              variant="h5"
              style={{
                textAlign: "center",
                width: "100%"
              }}
            >
              {t("bin")}{" "}
              {matchingNextBinInventory
                ? matchingNextBinInventory.bin.autostoreBin?.autostoreBinId
                : undefined}
            </Typography>
          ) : (
            ""
          )}
          <AutostoreBin
            state={
              !portOpened
                ? "Port Closed"
                : !binAtPort || portPollingActive
                  ? "Waiting for Bin"
                  : "Bin Opened"
            }
            pickQuantity={matchingNextBinInventory?.count?.value || ""}
            pickCompartment={compartment}
            numberOfColumns={
              autostoreBinConfiguration?.verticalCompartmentCount || 1
            }
            numberOfRows={
              autostoreBinConfiguration?.horizontalCompartmentCount || 1
            }
            binId={
              portOpened &&
              matchingNextBinInventory &&
              ((!portPollingActive && !shouldListenToGridEvents) ||
                (binAtPort && shouldListenToGridEvents))
                ? matchingNextBinInventory.bin.autostoreBin?.autostoreBinId
                : undefined
            }
            hideBinId
          />
        </Stack>
      </Stack>

      {/* right side */}
      <Box
        sx={{
          minHeight: 0,
          width: "100%",
          height: "100%",
          gridColumn: { xs: "1 / -1", md: "2 / 3" }
        }}
      >
        {/* open/new cycle count list */}
        {pageState === "initial" &&
          !!(
            matchingNextBinInventory ||
            loadingIncompleteCycleCounts ||
            incompleteCycleCounts.length
          ) && (
            <Stack direction="column" alignItems="center" gap={2} height="100%">
              <AutostoreTable<IncompleteCycleCountDto>
                headerColNames={[
                  t("product"),
                  t("sku"),
                  t("status"),
                  t("due by")
                ]}
                rowId={(row: IncompleteCycleCountDto) =>
                  row.productCycleId || row.cycleCountId || ""
                }
                renderColumns={[
                  (row: IncompleteCycleCountDto) =>
                    row.variant?.productName ?? "",
                  (row: IncompleteCycleCountDto) => row.variant?.sku || "",
                  (row: IncompleteCycleCountDto) => row.status || "",
                  (row: IncompleteCycleCountDto) =>
                    formatUtcDate(row.cycleEndsOn)
                ]}
                widthOfCols={["40%", "20%", "20%", "20%"]}
                headerVariant="overline"
                bodyVariant="body2"
                rowData={incompleteCycleCounts}
                selectedRows={selectedRows}
                selectRowCallback={(row: IncompleteCycleCountDto) => {
                  const rowId = row.productCycleId || row.cycleCountId || "";
                  if (selectedRows.includes(rowId)) {
                    dispatch(
                      selectRows(selectedRows.filter((id) => id !== rowId))
                    );
                  } else {
                    dispatch(selectRows([...selectedRows, rowId]));
                  }
                }}
                loading={false}
                tableWrapperSx={{
                  height: "100%",
                  overflow: "auto"
                }}
              />

              {totalPageCount > 1 && (
                <Pagination
                  count={totalPageCount}
                  page={page}
                  onChange={(_e, p) => {
                    dispatch(setPage(p));
                    if (window.scrollTo) window.scrollTo(0, 0);
                  }}
                  shape="rounded"
                />
              )}
            </Stack>
          )}
        {!matchingNextBinInventory &&
          !loadingIncompleteCycleCounts &&
          !incompleteCycleCounts.length && (
            <Typography variant="h5" align="center" sx={{ p: 2 }}>
              {t("no cycle counts found")}
            </Typography>
          )}
        {/* bin info for current bin */}
        {matchingUncountedBin && (
          <AutostoreTable<UncountedBinDto>
            headerColNames={[
              t("bin"),
              t("quantity"),
              t("holds"),
              t(inventoryDateLabel(inv_inventoryDateLabel))
            ]}
            rowId={(row: UncountedBinDto) => row.inventoryId}
            renderColumns={[
              (row) =>
                row.bin.autostoreBin?.autostoreBinId.toString() ||
                "no autostore bin",
              (row: UncountedBinDto) =>
                row.expectedCount?.value.toString() || "",
              (row) => {
                const inv = nextBinInventoryResponse.find(
                  (inventory) => inventory.inventoryId === row.inventoryId
                );
                if (inv?.holds.length) {
                  return (
                    <Typography
                      variant="body2"
                      style={{ textTransform: "capitalize" }}
                    >
                      {inv.holds.map((hold) => formatHoldText(hold)).join(", ")}
                    </Typography>
                  );
                }
                return "";
              },
              (row) =>
                checkIsExpiration(inv_inventoryDateLabel)
                  ? formatDateDayjs(row.expiration)
                  : formatDateDayjs(row.manufactureDate)
            ]}
            widthOfCols={["25%", "25%", "25%", "25%"]}
            headerVariant="overline"
            bodyVariant="body2"
            rowData={[matchingUncountedBin]}
            selectedRows={selectedBinRow ? [selectedBinRow] : undefined}
            selectRowCallback={(row: UncountedBinDto) => {
              const rowId = row.inventoryId;
              if (selectedBinRow === rowId) {
                dispatch(clearSelectedBinRow());
              } else {
                dispatch(selectBinRow(rowId));
              }
            }}
            loading={false}
          />
        )}
      </Box>

      {/* adjust inventory modal */}
      {invToModify && matchingUncountedBin && (
        <InventoryAdjustDialog
          open={isAdjustPanelOpen}
          onClose={() => dispatch(setIsAdjustPanelOpen(false))}
          invToModify={{
            ...invToModify,
            // use the uncounted bin expiration since it will be up to date if user adjusts date
            ...getInventoryDateObj(
              inv_inventoryDateLabel,
              dayjs(matchingUncountedBin?.expiration) ||
                dayjs(matchingUncountedBin?.manufactureDate)
            )
          }}
          selectedVariant={matchingUncountedBin.variant || null}
          refreshCb={({
            inventoryWasEmptied,
            newQuantity,
            inventoryId,
            newDate
          }) => {
            dispatch(setIsAdjustPanelOpen(false));
            if (
              (newQuantity || inventoryWasEmptied || newDate) &&
              inventoryId
            ) {
              const newDateStr = newDate ? formatDate(newDate) : newDate;
              props.modifyUncountedBin({
                inventoryId,
                cycleCountId: matchingUncountedBin.cycleCountId,
                newQuantity,
                newDate: checkIsExpiration(inv_inventoryDateLabel)
                  ? { expirationDate: newDateStr }
                  : { manufactureDate: newDateStr }
              });
            }
          }}
          canceledReason=""
        />
      )}

      {/* confirmation modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        noCancelButton
        confirmCb={() => {
          setIsConfirmModalOpen(false);
          resetPage();
        }}
        closeCb={() => {
          setIsConfirmModalOpen(false);
          resetPage();
        }}
        modalTitle={t("cycle count completed")}
        listToDisplay={completedCycleCountList}
        labelledById="cycle-count-completed-modal-title"
      />
      <InventoryHoldModal
        confirmCb={async (holdBin: HoldType) => {
          const { reasonCode } = holdBin;

          await props
            .placeInventoryHold(
              selectedInventory!.inventoryId,
              reasonCode,
              thisWorkstationId === undefined ? null : thisWorkstationId
            )
            .then(() => {
              dispatch(setIsBinHoldModalOpen(false));
            });
        }}
        open={isBinHoldModalOpen}
        closeModalCallback={() => dispatch(setIsBinHoldModalOpen(false))}
        maxWidth="sm"
        options={getHoldTypeOptions({
          excludeRecalledHold: ap_excludeRecalledHold
        })}
        confirmButtonText={t("set hold")}
        confirmActionTypeForTracking="Set Hold"
        currentHold=""
      />
    </Box>
  );
}

export default connector(AutostoreCycleCounts);
